import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Pencil, Phone, Plus, UserX, UserCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { API_URL, errMsg, godzinyApi } from './shared';
import { PortalSection } from './PortalSection';
import { WorkerDialog } from './WorkerDialog';

const WorkerCard = ({ w, canManage, onEdit, onChange }) => {
  const toggle = async () => {
    try {
      await axios.patch(`${API_URL}/godziny/workers/${w.id}`, { aktywny: !w.aktywny });
      toast.success(w.aktywny ? 'Pracownik dezaktywowany' : 'Pracownik aktywny');
      onChange();
    } catch (e) { toast.error(errMsg(e, 'Błąd')); }
  };
  return (
    <Card className={`bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4 space-y-3 ${w.aktywny ? '' : 'opacity-60'}`} data-testid={`worker-card-${w.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-semibold text-[var(--crm-text-primary)]">{w.name} {!w.aktywny && <span className="text-xs text-[var(--crm-text-muted)]">(nieaktywny)</span>}</p>
          <p className="text-sm text-[var(--crm-text-secondary)] flex items-center gap-1"><Phone className="w-3 h-3" />{w.telefon || '—'}</p>
        </div>
        <div className="text-right">
          <p className="text-lg font-bold text-[#FF6B35]" data-testid={`worker-rate-${w.id}`}>{w.stawka} zł/h</p>
          {canManage && (
            <div className="flex gap-1 justify-end mt-1">
              <Button size="sm" variant="ghost" onClick={() => onEdit(w)} data-testid={`worker-edit-${w.id}`}><Pencil className="w-4 h-4" /></Button>
              <Button size="sm" variant="ghost" onClick={toggle} data-testid={`worker-toggle-${w.id}`} title={w.aktywny ? 'Dezaktywuj' : 'Aktywuj'}>
                {w.aktywny ? <UserX className="w-4 h-4 text-red-400" /> : <UserCheck className="w-4 h-4 text-emerald-400" />}
              </Button>
            </div>
          )}
        </div>
      </div>
      <div className="text-xs text-[var(--crm-text-secondary)]" data-testid={`worker-rate-history-${w.id}`}>
        <span className="font-semibold">Historia stawek: </span>
        {w.rate_history.map((r) => `${r.stawka} zł/h od ${r.od}`).join(' · ') || '—'}
      </div>
      <PortalSection worker={w} canManage={canManage} onChange={onChange} />
    </Card>
  );
};

export const WorkersTab = ({ canManage }) => {
  const [workers, setWorkers] = useState([]);
  const [dialog, setDialog] = useState(null);
  const load = useCallback(() => godzinyApi.workers().then(setWorkers).catch(() => toast.error('Błąd pobierania pracowników')), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="workers-tab">
      {canManage && (
        <Button onClick={() => setDialog({ worker: null })} className="bg-[#FF6B35] hover:bg-[#e55a2b] text-white" data-testid="add-worker-btn">
          <Plus className="w-4 h-4 mr-1" />Dodaj pracownika
        </Button>
      )}
      {workers.length === 0 && <p className="text-[var(--crm-text-muted)]">Brak pracowników.</p>}
      <div className="grid gap-4 lg:grid-cols-2">
        {workers.map((w) => <WorkerCard key={w.id} w={w} canManage={canManage} onEdit={(x) => setDialog({ worker: x })} onChange={load} />)}
      </div>
      {dialog && <WorkerDialog open worker={dialog.worker} onClose={() => setDialog(null)} onSaved={load} />}
    </div>
  );
};
